import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import Carousel from '@wsb/guac-widget-shared/lib/components/Carousel';
import { OlsConfigStore } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import dataAids from '../../../../constants/dataAids';
import { hoverTextStyles, VIDEO_TYPE } from '../../../../constants/constants';
import imageCropOptions from '../../../../constants/imageCropOptions';
import ZoomLightbox from '../details/ZoomLightbox';
import Video from '../Video';
import { isZoomEnabled } from '../../utils/ProductUtils';
import ClassicCarousel from './Classic';
import FeaturedCarousel from './Featured';
import imageShapeOptions from '../../../../constants/imageShapeOptions';
import {
  VERTICAL_RATIO,
  HORIZONTAL_CLASSIC_RATIO, HORIZONTAL_HERO_RATIO, SQUARE_RATIO, getImageRatio } from '../../utils/ProductUtils';
import { LAYOUT1 } from '../../../../constants/pdpLayouts';

class CarouselChanger extends React.Component {
  componentDidUpdate() {
    const {
      goToSlide,
      selectedIndex,
      currentAssetIndex,
      assetChangedByVariantSelection
    } = this.props;

    if (assetChangedByVariantSelection && selectedIndex !== currentAssetIndex) {
      const direction = selectedIndex < currentAssetIndex ? 'right' : 'left';
      goToSlide(currentAssetIndex, direction);
    }
  }

  render() {
    return null;
  }
}

CarouselChanger.propTypes = {
  goToSlide: PropTypes.func,
  selectedIndex: PropTypes.number,
  currentAssetIndex: PropTypes.number,
  assetChangedByVariantSelection: PropTypes.any
};

export const MOBILE_CAROUSEL_HEIGHT = 300;
export const CLASSIC_CAROUSEL = 'classic';
export const FEATURED_CAROUSEL = 'featured';

const defaultLayout = CLASSIC_CAROUSEL;
const layouts = {
  [CLASSIC_CAROUSEL]: ClassicCarousel,
  [FEATURED_CAROUSEL]: FeaturedCarousel
};

const getStyles = () => ({
  thumbsetDots: {
    'position': 'relative',
    '@sm': { display: 'none' }
  }
});

class ProductDetailAssets extends Component {
  constructor(props) {
    super(props);

    this.state = { startVisibleAssetIndex: 0 };

    this.getCarouselArray = this.getCarouselArray.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener('resize', this.handleResize);
  }

  componentDidUpdate() {
    const {
      currentAssetIndex,
      assetChangedByVariantSelection,
      assetsVisible
    } = this.props;
    const { startVisibleAssetIndex } = this.state;

    if (
      assetChangedByVariantSelection &&
      !this.assetIsVisibleInStrip(currentAssetIndex, startVisibleAssetIndex)
    ) {
      let newStartIndex = 0;
      while (
        !this.assetIsVisibleInStrip(currentAssetIndex, newStartIndex) &&
        newStartIndex < currentAssetIndex
      ) {
        newStartIndex += assetsVisible;
      }

      if (this.assetIsVisibleInStrip(currentAssetIndex, newStartIndex)) {
        this.setState({
          startVisibleAssetIndex: newStartIndex
        });
      }
    }
  }

  handleResize = () => {
    if (!this._resizeTimer) {
      this._resizeTimer = setTimeout(function () {
        this._resizeTimer = null;
        // Force re-render of component
        if (this._isMounted) {
          this.forceUpdate();
        }
      }.bind(this), 66);
    }
  }

  assetIsVisibleInStrip(indexToFind, startIndex) {
    const { assetsVisible } = this.props;
    return indexToFind >= startIndex && indexToFind <= startIndex + assetsVisible - 1;
  }

  handleAssetStripChange = (direction) => {
    // when a user clicks to move the asset strip, we want to reset the
    // assetChangedByVariantSelection variable in case we just selected an
    // asset by selecting a variant. This will prevent us from checking to
    // see if the asset is visible in the strip and potentially not moving
    // the strip when we actually should
    const { currentAssetIndex, onClickThumbnail, assetsVisible } = this.props;
    onClickThumbnail(currentAssetIndex);

    const { startVisibleAssetIndex } = this.state;
    const change = direction === 'right' ? assetsVisible : -assetsVisible;
    let newStartIndex = startVisibleAssetIndex + change;
    if (newStartIndex < 0) newStartIndex = 0;

    this.setState({
      startVisibleAssetIndex: newStartIndex,
      showImageZoom: false
    });
  }

  getShapeHeightMultiplier(shape, layout) {
    /**
     * Sets a multiplier to acomodate the image container based on the shape
     * if set to horizontal it will need less height than vertical,
     * so we multiply the MOBILE_CAROUSEL_HEIGHT * 0.75 (which is the current vertical aspect ratio) = 225px
     * same applies for vertical, it needs more height so we multiply the MOBILE_CAROUSEL_HEIGHT * 1.33 (which is the current horizontal aspect ratio) = 399px
     * with this, the container will mantain desktop aspect ratios.
    */
    if (shape === imageShapeOptions.HORIZONTAL) return VERTICAL_RATIO;
    if (shape === imageShapeOptions.VERTICAL) {
      return layout === LAYOUT1 ? HORIZONTAL_CLASSIC_RATIO : HORIZONTAL_HERO_RATIO;
    }

    return SQUARE_RATIO;
  }

  renderMobileAssets(translate) {
    const productAssets = this.assets;
    const {
      renderMode,
      onClickThumbnail,
      currentAssetIndex,
      assetChangedByVariantSelection,
      pdpImageCropMethod,
      pdpImageShape,
      pdpLayout
    } = this.props;
    const expandToFill = pdpImageCropMethod === imageCropOptions.EXPAND_TO_FILL;
    const hasMoreThanOneProduct = productAssets.length > 1;
    const heightMultiplier = this.getShapeHeightMultiplier(pdpImageShape, pdpLayout);
    const carouselHeight = (hasMoreThanOneProduct || expandToFill) ? `${MOBILE_CAROUSEL_HEIGHT * heightMultiplier}px` : 'auto';
    const carouselContainerHeight = carouselHeight;
    const controls = [
      {
        component: CarouselChanger,
        props: { currentAssetIndex, assetChangedByVariantSelection }
      }
    ];

    return (
      <Carousel
        dots={ hasMoreThanOneProduct }
        infinite={ hasMoreThanOneProduct }
        draggable={ hasMoreThanOneProduct }
        arrows={ false }
        renderMode={ renderMode }
        cellPadding={ 2 }
        slideWidth='100%'
        viewportHeight={ carouselContainerHeight }
        controls={ controls }
        afterChange={ onClickThumbnail }
        children={ this.getCarouselArray(carouselHeight, translate) }
      />
    );
  }

  getScaledImage(image) {
    const { pdpImageCropMethod, pdpImageShape, pdpLayout } = this.props;
    const assetApiUrl = /[rs=w:](\d+)/.exec(image);
    if (assetApiUrl) {
      const curSize = +assetApiUrl[1];
      const size = curSize * getImageRatio(pdpImageShape, pdpLayout);
      if (pdpImageCropMethod === imageCropOptions.EXPAND_TO_FILL) {
        return image.replace(`w:${curSize},h:${curSize}`, `w:${size},h:${size},cg:true`);
      }
    }

    return image;
  }

  getCarouselArray(carouselHeight) {
    const { product, onClickThumbnail, currentAssetIndex, renderMode, pdpImageCropMethod } = this.props;
    const styles = {
      carouselItem: {
        display: 'flex !important',
        width: '100%',
        textAlign: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        height: carouselHeight
      },
      carouselImage: {
        display: 'block',
        width: '100%',
        height: '100%',
        flexShrink: 0,
        objectFit: 'contain'
      }
    };

    // Only enable the mobile zoom on published sites due poor UX in editor
    const enableMobileZoom = this.enableOlsImageZoom && renderMode === 'PUBLISH';
    const { Block, Image } = UX2.Element;

    return this.assets.map((assetData, index) => {
      let component;
      const zoomEnabled = this.enableOlsImageZoom && isZoomEnabled(assetData);
      const { type, large_url: videoUrl } = assetData;

      if (type === VIDEO_TYPE) {
        component = (<Video videoUrl={ videoUrl }/>);
      } else {
        const isNebula = videoUrl && videoUrl.indexOf('isteam') > -1;
        const expandToFill = pdpImageCropMethod === imageCropOptions.EXPAND_TO_FILL;
        if (isNebula && expandToFill) {
          styles.carouselImage = {
            ...styles.carouselImage,
            objectFit: 'cover'
          };
        }
        const handleClick = enableMobileZoom && zoomEnabled ? () => {
          if (currentAssetIndex !== index) { onClickThumbnail(index); }
          this.setState({ showImageZoom: true });
        } : () => {};

        const scaledImage = this.getScaledImage(videoUrl);

        component = (<Image
          src={ scaledImage }
          alt={ product.name }
          data-aid={ dataAids.PRODUCT_CAROUSEL_ASSET_RENDERED }
          style={ styles.carouselImage }
          onClick={ handleClick }
        />);
      }

      return (
        <Block>
          <Block key={ index } style={ styles.carouselItem }>
            {component}
          </Block>
        </Block>
      );
    });
  }

  get assets() {
    const { product } = this.props;
    const productAssets = product.assets || product.images;
    if (productAssets && productAssets.length > 0) return productAssets;
    const noProductImage = OlsConfigStore.getState().noProductImage;

    return (
      [{
        small_url: noProductImage,
        large_url: noProductImage,
        attachment_height: 600,
        attachment_width: 600
      }]
    );
  }

  get selectedAsset() {
    const { currentAssetIndex } = this.props;
    const {
      large_url: currentAssetUrl,
      zoom_image_url: zoomAssetUrl,
      attachment_width: attachmentWidth,
      attachment_height: attachmentHeight,
      type
    } = this.assets[currentAssetIndex];
    return {
      currentAssetUrl,
      zoomAssetUrl: zoomAssetUrl || currentAssetUrl,
      attachmentWidth,
      attachmentHeight,
      type
    };
  }

  get enableOlsImageZoom() {
    const { enableOlsImageZoom = false } = (this.context.featureFlags || {});
    return enableOlsImageZoom;
  }

  renderCarousel({ layout, ...props }) {
    const ProductDetailsAssetsLayout = layouts[layout] || layouts[defaultLayout];
    return <ProductDetailsAssetsLayout { ...props } />;
  }

  render() {
    const translate = this.context.translate || (() => {});
    const {
      layout,
      product,
      pdpImageCropMethod,
      renderMode,
      currentAssetIndex,
      assetsVisible,
      fetching,
      onClickThumbnail,
      pdpImageShape,
      pdpLayout
    } = this.props;
    const { Block } = UX2.Element;

    if (currentAssetIndex && currentAssetIndex >= this.assets.length) {
      return null;
    }

    const { startVisibleAssetIndex } = this.state;

    const finalizedAssets = this.assets;
    const { currentAssetUrl, zoomAssetUrl } = this.selectedAsset;

    const zoomEnabled = this.enableOlsImageZoom && isZoomEnabled(finalizedAssets[currentAssetIndex]);

    const assetRatios = finalizedAssets.map(assetData => {
      return assetData.attachment_height / assetData.attachment_width;
    });

    const assetWidths = finalizedAssets.map(assetData => {
      return assetData.attachment_width;
    });
    const tallestAsset = Math.min(Math.max(...assetRatios) * 100, 100);
    const paddingTop = this.selectedAsset.type === VIDEO_TYPE ? '56.25' : tallestAsset;
    const widestAsset = Math.max(...assetWidths);

    const styles = getStyles();

    const desktopHintTextStyles = {
      ...hoverTextStyles,
      visibility: zoomEnabled ? 'visible' : 'hidden'
    };

    const hasFinalizedAssets = !fetching && finalizedAssets.length > 1;
    const isNotFetching = !fetching && finalizedAssets.length > assetsVisible;
    const lastIndex = finalizedAssets.length - assetsVisible;
    const layoutProps = {
      assets: this.assets,
      desktopHintTextStyles,
      hasFinalizedAssets,
      isAssetVisibleInStrip: index => (
        this.assetIsVisibleInStrip(index, startVisibleAssetIndex)
      ),
      isCurrentAsset: index => currentAssetIndex === index,
      isNotFirst: isNotFetching && startVisibleAssetIndex > 0,
      isNotLast: isNotFetching && startVisibleAssetIndex < lastIndex,
      layout,
      onAssetStripChangeLeft: () => this.handleAssetStripChange('left'),
      onAssetStripChangeRight: () => this.handleAssetStripChange('right'),
      onClickThumbnail,
      paddingTop,
      pdpImageCropMethod,
      pdpImageShape,
      product: this.props.product,
      selectedAsset: this.selectedAsset,
      tallestAsset,
      translate,
      widestAsset,
      zoomEnabled,
      pdpLayout
    };

    const mobileContainerPadding = layout === CLASSIC_CAROUSEL ? '0' : 'medium';

    return (
      <Block style={{ 'paddingBottom': 'medium', '@xs-only': { paddingBottom: mobileContainerPadding } }}>
        <Block style={{ '@xs-only': { display: 'none' } }}>
          { this.renderCarousel(layoutProps) }
        </Block>
        <Block style={ styles.thumbsetDots }>
          { this.renderMobileAssets(translate)}
        </Block>
        <ZoomLightbox
          src={ currentAssetUrl }
          srcLarge={ zoomAssetUrl }
          alt={ product.name }
          visible={ this.state.showImageZoom }
          onClose={ () => this.setState({ showImageZoom: false }) }
          renderMode={ renderMode }
        />
      </Block>
    );
  }
}

ProductDetailAssets.propTypes = {
  layout: PropTypes.string,
  assetsVisible: PropTypes.number,
  assets: PropTypes.array,
  assetChangedByVariantSelection: PropTypes.any,
  currentAssetIndex: PropTypes.number,
  onClickThumbnail: PropTypes.func,
  renderMode: PropTypes.string,
  fetching: PropTypes.bool,
  product: PropTypes.object,
  pdpImageCropMethod: PropTypes.string,
  pdpImageShape: PropTypes.string,
  pdpLayout: PropTypes.string.isRequired
};


ProductDetailAssets.defaultProps = {
  layout: defaultLayout,
  assetsVisible: 4,
  assets: [],
  fetching: false,
  pdpImageCropMethod: imageCropOptions.SHRINK_TO_FIT
};

ProductDetailAssets.contextTypes = {
  translate: PropTypes.func,
  featureFlags: PropTypes.object
};

export default ProductDetailAssets;
